import type {
  CodingAgentsVm,
  CodingLoaded,
  ConsentRequest,
  ConsentRequestStatus,
  DecisionResult,
  RevokeResult,
} from './coding-agents-types';

/**
 * Where Coding agents gets its records. F1 and ART register the real routes;
 * until then the section stays hidden (the default source reports
 * "unavailable"). No URL is assumed here.
 */
export interface CodingAgentsSource {
  list(): Promise<CodingLoaded<CodingAgentsVm>>;
  loadRequest(requestId: string): Promise<CodingLoaded<ConsentRequest>>;
  /** Sends exactly the person's decision; the server applies current rights. */
  /**
   * `approvedScopeIds` are exactly the scopes this card showed as within the
   * person's rights when it was loaded, asked or already held, because a
   * consent restates the whole grant (empty for a deny). The
   * adapter sends those and the page's CSRF, never a scope that became
   * available after the load; the server still rechecks current rights.
   */
  decide(
    requestId: string,
    decision: 'allow' | 'deny',
    approvedScopeIds: string[],
  ): Promise<DecisionResult>;
  revoke(grantId: string): Promise<RevokeResult>;
  /** Mirror of F1's single-use request fact (pending F1's ACK); unreadable is `unknown`. */
  requestStatus(requestId: string): Promise<ConsentRequestStatus>;
}

const unavailable: CodingAgentsSource = {
  list: async () => ({ status: 'unavailable' }),
  loadRequest: async () => ({ status: 'unavailable' }),
  decide: async () => 'failed',
  revoke: async () => 'failed',
  requestStatus: async () => ({ state: 'unknown' }),
};

let current: CodingAgentsSource = unavailable;
export const codingAgentsSource = () => current;
export function registerCodingAgentsSource(source: CodingAgentsSource | null) {
  current = source ?? unavailable;
}
